import { Dictionary } from 'lodash';
import { Moment } from 'moment';
import {
  DateType,
  Team as TeamT,
  User as UserT,
  Role as RoleT,
  Company,
} from 'api/models';
import { Project } from 'api/project';
import { Task as TaskT } from 'api/tasks';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import {
  AllocatedMinutes,
  AvailabilityFilter,
  Range,
} from 'components/organisms/resource-management/types';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { Minutes } from 'components/organisms/user-capacity';

export enum ResourceManagementGroupBy {
  Users = 'PEOPLE',
  Projects = 'PROJECT',
}
export enum ResourceType {
  User = 'user',
  Team = 'team',
  Role = 'role',
}

export enum AllocationMedium {
  Soft = 'SOFT',
  Hard = 'HARD',
}

export enum AllocationMediumApi {
  Soft = 'soft',
  Hard = 'hard',
}

export interface UserIdentifier {
  type: ResourceType.User;
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
}

export interface TeamIdentifier {
  type: ResourceType.Team;
  teamId: number;
}

export interface RoleIdentifier {
  type: ResourceType.Role;
  roleId: number;
  teamId: number;
}

export type ResourceIdentifier =
  | UserIdentifier
  | TeamIdentifier
  | RoleIdentifier;

export type SummaryResourceIdentifer = UserIdentifier | RoleIdentifier;

interface AllocationBase {
  id: number;
  startDate: DateType;
  endDate: DateType;
  minutes: number;
  totalMinutes: number;
  projectPhaseId?: number;
  billable: boolean;
  projectId: number;
  medium: AllocationMedium;
  sources?: AllocationSourceTask[];
}
interface AllocationWithUserId extends AllocationBase {
  type: ResourceType.User;
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
  overAllocations: OverAllocationApi[];
}
interface AllocationWithTeamId extends AllocationBase {
  type: ResourceType.Team;
  teamId: number;
  roleId: number;
}

export type Allocation = AllocationWithUserId | AllocationWithTeamId;

export type AllocationCreatePayload =
  | Omit<AllocationWithUserId, 'id'>
  | Omit<AllocationWithTeamId, 'id'>;

export type AllocationUpdatePayload = Allocation;

export type AllocationDeletePayload = Allocation;

export type AllocationBulkPayload = {
  create?: AllocationCreatePayload[];
  update?: AllocationUpdatePayload[];
  delete?: AllocationDeletePayload[];
};

interface AllocationResponseBase extends AllocationInDetailBase {
  project: Project;
}
interface AllocationResponseWithUser extends AllocationResponseBase {
  user: UserT;
}
interface AllocationResponseWithTeam extends AllocationResponseBase {
  team: TeamT;
}

type AllocationResponse =
  | AllocationResponseWithUser
  | AllocationResponseWithTeam;

export interface AllocationBulkResponse {
  allocations: {
    allocation: AllocationResponse;
    status: boolean;
  }[];
}
export type AggregationMediumMinutes = {
  type: AllocationMedium;
  totalMinutes: number;
};
export interface AggregationBase {
  projectId: number;
  totalMinutes?: number; // TODO: Made this optional as we receive from BE, but not use in FE anymore
  mediumMinutes?: {
    types: AggregationMediumMinutes[];
  };
  allocatedMintutes: AllocatedMinutes;
  minStartDate?: string | null;
  maxEndDate?: string | null;
}
export interface UserLevelAggregation extends AggregationBase {
  userId: number;
  type: ResourceType.User;
  roleId?: number;
  referenceTeamId?: number;
}
export interface TeamLevelAggregation extends AggregationBase {
  teamId: number;
  type: ResourceType.Team;
}
export interface AllocationInDetailBase {
  id: number;
  startDate: DateType;
  endDate: DateType;
  minutes: number;
  projectPhase?: {
    id: number;
    name: string;
  };
  billable: boolean;
  medium: AllocationMedium;
}

export interface AllocationForTeam extends AllocationInDetailBase {
  type: ResourceType.Team;
  team: Pick<TeamT, 'teamId'>;
  project: Project;
}

export interface AllocationForProjectWithUser extends AllocationInDetailBase {
  type: ResourceType.User;
  user: UserT;
  role?: RoleT;
  referenceTeam?: TeamT;
}
export interface AllocationForProjectWithTeam extends AllocationInDetailBase {
  type: ResourceType.Team;
  team: TeamT;
}

export type AllocationForProject =
  | AllocationForProjectWithUser
  | AllocationForProjectWithTeam;

export interface AllocationsGroupByProject {
  project: Project;
  allocations: AllocationForProject[];
  aggregations: (UserLevelAggregation | TeamLevelAggregation)[];
}

export interface UserIdWithAvailableMinutes {
  user: Pick<UserT, 'userId'>;
  fulfilledMinutes: number;
  utilization?: number;
  margin?: number;
  cost?: number;
}

export interface AvailabilityForTeamWithProject {
  project: Pick<Project, 'projectId'>;
  team: Pick<TeamT, 'teamId'>;
  requiredMinutes: number;
  matches: UserIdWithAvailableMinutes[];
}

export interface UserSocketIdentifier extends Pick<UserT, 'userId'> {
  identityType: 'USER';
}

export interface TeamSocketIdentifier extends Pick<TeamT, 'teamId'> {
  identityType: 'TEAM';
}

export interface CompanySocketIdentifier extends Pick<Company, 'companyId'> {
  identityType: 'COMPANY';
}

type TeamWithId = TeamT & TeamSocketIdentifier;

export interface TeamWithAdditionalInfo
  extends Omit<TeamWithId, 'userList' | 'companyList'> {
  accountId: number;
  userList?: UserSocketIdentifier[];
  companyList?: CompanySocketIdentifier[];
}

export interface TeamReplacementSocketData extends TeamSocketIdentifier {
  accountId: number;
  userList?: UserSocketIdentifier[];
  companyList?: CompanySocketIdentifier[];
}

export type SocketDataForTeam = TeamSocketIdentifier | TeamWithAdditionalInfo;

export interface SocketDataForTeamInRm {
  team: SocketDataForTeam;
}

/** Resource Management Config */
export interface SoftAllocationConfig {
  enabled: boolean;
  meta: {
    templateDefault: boolean;
  };
}

export interface CollaboratorAccessConfig {
  hasViewAccess: boolean;
  hasEditAccess: boolean;
}

export interface AccessConfig {
  collaboratorAccessMeta: CollaboratorAccessConfig;
}

export enum PlanningLevelConfig {
  HoursPerDay = 'HOURS_PER_DAY',
  HoursPerWeek = 'HOURS_PER_WEEK',
}

export interface ResourceManagementConfig {
  softAllocations: SoftAllocationConfig;
  accessConfig: AccessConfig;
  planningLevelConfig: PlanningLevelConfig;
}

export enum ResourceManagementApiZoomType {
  Day = 'DAY',
  Week = 'WEEK',
  Month = 'MONTH',
}
export interface ResourceManagementExportParams {
  startDate: Moment;
  endDate: Moment;
  groupBy: ResourceManagementGroupBy;
  zoomType: ResourceManagementApiZoomType;
  filter: GroupedFilter;
}

export interface ResourceManagementFilterParams {
  groupBy: ResourceManagementGroupBy;
  filter: GroupedFilter;
}

export interface FilteredProjectInPeople {
  projectId: number;
  referenceTeamId?: number;
  roleId?: number;
}

export interface FilteredUserInPeople {
  type: ResourceType.User;
  userId: number;
  projects: FilteredProjectInPeople[];
}

export interface FilteredTeamInPeople {
  projectId: number;
  teamId: number;
}

export interface FilteredRoleInPeople {
  type: ResourceType.Role;
  roleId: number;
  projects: FilteredTeamInPeople[];
}

export interface FilteredResponseInPeople {
  peopleTabEntities: (FilteredUserInPeople | FilteredRoleInPeople)[];
}

export interface FilteredResourceInProjects {
  projectId: number;
}

export interface FilteredResponseInProjects {
  projectTabEntities: FilteredResourceInProjects[];
}

export type ResourceManagementFilterResponse =
  | FilteredResponseInPeople
  | FilteredResponseInProjects;

export interface DeleteAllocationsProps {
  projectId: number;
  resourceIdentifier: SummaryResourceIdentifer;
}

export interface ConvertAllocationsProps {
  projectId: number;
  resourceIdentifier?: SummaryResourceIdentifer; // resourceIdentifier is not needed for project-level conversion
  events?: AllocationEventModel[];
}

export interface ConvertAllocationsPropsApi extends ConvertAllocationsProps {
  medium: AllocationMedium;
}

/** V2 Types */
export interface ResourceAllocationApiDateRange {
  startDate: DateType;
  endDate: DateType;
}
export interface GetResourceAllocationPayload
  extends ResourceAllocationApiDateRange {
  groupBy?: ResourceManagementGroupBy;
  includeSummaries?: boolean;
  ignoreSoftAllocation?: boolean;
  includeOverAllocations?: boolean;
  includeEmptySummaries?: boolean;
  userIdList?: number[];
  roleIdList?: number[];
  projectIdList?: number[];
}

export interface GetResourceAllocationDemandPayload
  extends ResourceAllocationApiDateRange {
  roleIdList: number[];
  zoomLevel: ResourceManagementApiZoomType;
  ignoreSoftAllocation: boolean;
  filter: GroupedFilter;
}

export enum FetchableResourceType {
  UserList = 'userIdList',
  RoleList = 'roleIdList',
  ProjectList = 'projectIdList',
}

export type ListOfResourcesToBeFetched = [number] | number[];

export type TypesOfResourcesToBeFetched =
  | Dictionary<boolean>
  | ListOfResourcesToBeFetched;

export type ResourcesToBeFetched<T extends TypesOfResourcesToBeFetched> = {
  [Property in FetchableResourceType]: T;
};

export type ResourcesLoadedInCurrentView = ResourcesToBeFetched<
  Dictionary<boolean>
>;

export interface LoadAllocationsData<T extends ListOfResourcesToBeFetched> {
  resources: ResourcesToBeFetched<T>;
  range: Range;
}

export interface AllocatedMinutesApi {
  [AllocationMediumApi.Hard]: Minutes;
  [AllocationMediumApi.Soft]: Minutes;
}

export interface OverAllocationApi {
  startDate: DateType;
  endDate: DateType;
  overAllocatedMinutes: Minutes;
}

interface BaseResourceSummary {
  startDate: DateType;
  endDate: DateType;
  allocatedMinutes: AllocatedMinutesApi;
}

export type TeamResourceSummary = BaseResourceSummary;

export type CompanyResourceSummary = BaseResourceSummary;

export interface UserResourceSummary extends BaseResourceSummary {
  capacityMinutes: Minutes;
}

export interface AllocationForUser extends AllocationInDetailBase {
  type: ResourceType.User;
  user: Pick<UserT, 'userId'>;
  project: Project;
  role?: RoleT;
  referenceTeam?: TeamT;
  overAllocations: OverAllocationApi[];
}

export interface AllocationsGroupByUser {
  type: ResourceType.User;
  user: UserT;
  allocations: AllocationForUser[];
  summaries: UserResourceSummary[];
  aggregations: UserLevelAggregation[];
  companySummaries: CompanyResourceSummary[];
}

export interface AllocationsGroupByUserLookup
  extends Omit<AllocationsGroupByUser, 'aggregations'> {
  aggregationLookup: Dictionary<UserLevelAggregation>;
}

export interface AllocationsGroupByTeam {
  type: ResourceType.Team;
  team: TeamT;
  allocations: AllocationForTeam[];
  summaries: TeamResourceSummary[];
  aggregations: TeamLevelAggregation[];
  companySummaries: CompanyResourceSummary[];
}
export interface AllocationsGroupByTeamLookup
  extends Omit<AllocationsGroupByTeam, 'aggregations'> {
  aggregationLookup: Dictionary<TeamLevelAggregation>;
}

export interface AllocationsGroupByUsersOrTeams {
  resources: (AllocationsGroupByUser | AllocationsGroupByTeam)[];
  groupBy: ResourceManagementGroupBy.Users;
  startDate: DateType;
  endDate: DateType;
  zoomLevel: ResourceManagementApiZoomType;
}
export type AllocationsGroupByUsersOrTeamsLookup = Dictionary<
  AllocationsGroupByUserLookup | AllocationsGroupByTeamLookup
>;

export type ResourceAllocations = (
  | AllocationsGroupByUser
  | AllocationsGroupByTeam
)[];

export type AvailabilityFilterApi = Omit<AvailabilityFilter, 'roles'>;

export type AvailabilityFilterApiWithPlanningLevel = AvailabilityFilterApi & {
  isHoursPerWeek: boolean;
  ignoreSoftAllocation?: boolean;
};

export interface DemandSummary {
  userSummary: AllocatedMinutesApi;
  teamSummary: AllocatedMinutesApi;
}

export interface CapacitySummary {
  teamMembersCapacityFte: number;
  partnerCapacityFte: number;
  timeOffFte: number;
  holidaysFte: number;
}

export interface DemandTabProjects extends Pick<Project, 'projectId'> {
  allocationFte: number;
  softAllocationFte?: number;
  hardAllocationFte?: number;
}
export interface DemandResponseUnit extends ResourceAllocationApiDateRange {
  capacityMinutes: Minutes;
  summary: DemandSummary;
  capacityDetails: CapacitySummary;
  totalAllocationFte: number;
  teamAllocationFte: number;
  userAllocationFte: number;
  projectAllocationSummaryList: DemandTabProjects[];
}

export interface RoleWiseDemandResponse {
  role: RoleT;
  demandResponseUnits: DemandResponseUnit[];
  projectIdToAllocationFteMap: Record<number, number>;
  totalAllocationForRoleFte: number;
  totalCapacityForRoleFte: number;
}

export interface DemandGraphUnits {
  startDate: string;
  endDate: string;
  holidayFte: number;
  timeOffFte: number;
  availableCapacityFte: number;
  userAllocationFte: number;
  teamAllocationFte: number;
  actualCapacityFte: number;
}

export interface ResourceAllocationDemandResponse
  extends Omit<GetResourceAllocationDemandPayload, 'roleIdList' | 'filter'> {
  roleWiseDemandResponses: RoleWiseDemandResponse[];
}

export interface ResourceAllocationDemandGraphResponse
  extends Omit<GetResourceAllocationDemandPayload, 'roleIdList' | 'filter'> {
  demandTabGraphResponseUnits: DemandGraphUnits[];
}

export enum AllocationSourceType {
  Task = 'TASK',
}

export interface AllocationSourceTask {
  sourceType: AllocationSourceType.Task;
  sourceId: number;
}

export interface AllocationSource extends AllocationSourceTask {
  metaData: TaskT;
}
export interface AllocatedDailyMinutes {
  dailyAllocatedMinutes: Map<string, number>;
  projectId: number;
}
